import { APIEntity } from 'services/api/entities'
import { RAChoiceInputChoice, ValueOf } from 'types/common'
import { activityStatusChoices, transactionStatusChoices } from './form'

export type FilterInputType =
  | 'date'
  | 'number'
  | 'text'
  | 'organizationFilter'
  | 'clientFilter'
  | 'select'
  | 'visibleToAll'

export type FiltersCfg = {
  source: ValueOf<typeof FILTER_SOURCES>
  label: string
  inputField: FilterInputType
  displayAlways?: boolean
  additionalProps?: {
    choices?: RAChoiceInputChoice[]
    // Default value shouldn't be set on an input level, but as a prop passed to the List component
    // defaultValue?: any
  }
}

export const FILTER_SOURCES = {
  firstName: 'firstName~contains~',
  lastName: 'lastName~contains~',
  name: 'name~contains~',
  customerName: 'customerName~contains~',
  dateStart: 'startDate~gte~',
  dateEnd: 'startDate~lt~',
  eventDateStart: 'eventDate~gte~',
  eventDateEnd: 'eventDate~lt~',
  status: 'status~eq~',
  eventStatus: 'eventStatus~eq~',
  customerId: 'clientId~eq~',
  organizationId: 'organizationId~eq~',
  visibleToAll: 'isPublic~eq~',
  ids: 'id~in~',
  nickname: 'nickname~contains~',
  capacity: 'capacity~gte~',
  peopleCapacity: 'peopleCapacity~gte~',
  roomCapacity: 'roomCapacity~gte~',
  supplierResponsibleFullName: 'supplierName~contains~',
  facetCode: 'facetCode~contains~',
} as const

export type FilterSource = ValueOf<typeof FILTER_SOURCES>

export const NON_STRING_FILTER_SOURCES: FilterSource[] = [
  FILTER_SOURCES.dateEnd,
  FILTER_SOURCES.dateStart,
  FILTER_SOURCES.eventDateStart,
  FILTER_SOURCES.eventDateEnd,
]

export const DATE_RANGE_END_FILTER_SOURCES: FilterSource[] = [
  FILTER_SOURCES.dateEnd,
  FILTER_SOURCES.eventDateEnd,
]

type FilterCfgMap = Partial<Record<APIEntity, FiltersCfg[]>>

const EVENT_FILTERS: FiltersCfg[] = [
  {
    source: FILTER_SOURCES.customerName,
    inputField: 'text',
    label: 'Client Name',
    displayAlways: true,
  },
  {
    source: FILTER_SOURCES.dateStart,
    inputField: 'date',
    label: 'Date From',
    displayAlways: false,
  },
  {
    source: FILTER_SOURCES.dateEnd,
    inputField: 'date',
    label: 'Date To',
    displayAlways: false,
  },
]

const USER_FILTERS: FiltersCfg[] = [
  {
    source: FILTER_SOURCES.firstName,
    inputField: 'text',
    label: 'First Name',
    displayAlways: true,
  },
  {
    source: FILTER_SOURCES.lastName,
    inputField: 'text',
    label: 'Last Name',
    displayAlways: true,
  },
]

const CUSTOMER_FILTERS: FiltersCfg[] = [
  {
    label: 'Business Name',
    source: FILTER_SOURCES.name,
    inputField: 'text',
    displayAlways: true,
  },
]

const UPSELLING_FILTERS: FiltersCfg[] = [
  {
    label: 'Name',
    source: FILTER_SOURCES.name,
    inputField: 'text',
    displayAlways: true,
  },
]

const TRANSACTION_FILTERS: FiltersCfg[] = [
  {
    source: FILTER_SOURCES.eventStatus,
    label: 'Event Status',
    inputField: 'select',
    displayAlways: true,
    additionalProps: {
      choices: transactionStatusChoices,
    },
  },
  {
    source: FILTER_SOURCES.customerName,
    inputField: 'text',
    label: 'Customer name',
    displayAlways: true,
  },
  {
    source: FILTER_SOURCES.eventDateStart,
    inputField: 'date',
    label: 'Date From',
    displayAlways: false,
  },
  {
    source: FILTER_SOURCES.eventDateEnd,
    inputField: 'date',
    label: 'Date To',
    displayAlways: false,
  },
]

const TAILORED_FILTERS: FiltersCfg[] = [
  {
    source: FILTER_SOURCES.customerId,
    inputField: 'clientFilter',
    label: 'Only for (Client)',
    displayAlways: false,
  },
  {
    source: FILTER_SOURCES.organizationId,
    inputField: 'organizationFilter',
    label: 'Only for (Organization)',
    displayAlways: false,
  },
  {
    source: FILTER_SOURCES.visibleToAll,
    inputField: 'visibleToAll',
    label: 'Visible to All',
    displayAlways: true,
  },
]

const LOCATION_FILTERS: FiltersCfg[] = [
  {
    source: FILTER_SOURCES.name,
    inputField: 'text',
    label: 'Name',
    displayAlways: true,
  },
  {
    label: 'Nickname',
    source: FILTER_SOURCES.nickname,
    inputField: 'text',
    displayAlways: false,
  },
  {
    label: 'Supplier Responsible Name',
    source: FILTER_SOURCES.supplierResponsibleFullName,
    inputField: 'text',
    displayAlways: false,
  },
  {
    label: 'People Capacity',
    source: FILTER_SOURCES.peopleCapacity,
    inputField: 'number',
    displayAlways: false,
  },
  {
    label: 'Room Capacity',
    source: FILTER_SOURCES.roomCapacity,
    inputField: 'number',
    displayAlways: false,
  },
  {
    inputField: 'select',
    label: 'Status',
    displayAlways: false,
    source: FILTER_SOURCES.status,
    additionalProps: {
      choices: activityStatusChoices,
    },
  },
  {
    inputField: 'text',
    label: 'Facet Code',
    displayAlways: false,
    source: FILTER_SOURCES.facetCode,
  },
  ...TAILORED_FILTERS,
]

export const filterConfig: FilterCfgMap = {
  drinks: TAILORED_FILTERS,
  eventpackages: TAILORED_FILTERS,
  events: EVENT_FILTERS,
  eventsdraft: EVENT_FILTERS,
  foods: TAILORED_FILTERS,
  kampaayers: USER_FILTERS,
  locations: LOCATION_FILTERS,
  locationupselling: UPSELLING_FILTERS,
  musics: TAILORED_FILTERS,
  physicals: TAILORED_FILTERS,
  pmi: CUSTOMER_FILTERS,
  professionals: TAILORED_FILTERS,
  rents: TAILORED_FILTERS,
  suppliers: CUSTOMER_FILTERS,
  transaction: TRANSACTION_FILTERS,
  virtuals: TAILORED_FILTERS,
}
